import ReconnectingWebSocket from 'reconnecting-websocket';
import type { WebSocketEventMap } from './websocket.events';
import type { WSMessage } from './websocket.types';

export type Listener<K extends keyof WebSocketEventMap> =
    (data: WebSocketEventMap[K]) => void;

export class WebSocketManager {
    private socket: ReconnectingWebSocket | null = null;

    private listeners: {
        [K in keyof WebSocketEventMap]?: Set<Listener<K>>;
    } = {};

    private getListenersForEvent<K extends keyof WebSocketEventMap>(
        event: K
    ): Set<Listener<K>> {
        const existingListeners = this.listeners[event] as Set<Listener<K>> | undefined;
        if (existingListeners) {
            return existingListeners;
        }

        const nextListeners = new Set<Listener<K>>();
        this.listeners[event] = nextListeners as unknown as (typeof this.listeners)[K];
        return nextListeners;
    }

    connect(url: string): void {
        if (this.socket) return;

        this.socket = new ReconnectingWebSocket(url, [], {
            maxReconnectionDelay: 10000,
            minReconnectionDelay: 2000,
            reconnectionDelayGrowFactor: 1.5,
            minUptime: 5000,
            connectionTimeout: 4000,
            maxRetries: 10,
            maxEnqueuedMessages: 100,
            startClosed: false,
        });

        this.socket.onopen = () => {
            this.emit('connect', undefined);
        };

        this.socket.onclose = () => {
            this.emit('disconnect', undefined);
        };

        this.socket.onmessage = (event: MessageEvent<string>) => {
            try {
                const parsed: WSMessage = JSON.parse(event.data);

                this.emit('message', parsed);

                if (parsed.messageType === 'data_flow') {
                    this.emit('data_flow', parsed);
                }

                if (parsed.messageType === 'dq_stats') {
                    this.emit('dq_stats', parsed);
                }
            } catch (error) {
                console.error('WebSocket parse error:', error);
            }
        };
    }

    disconnect(): void {
        this.socket?.close();
        this.socket = null;
    }

    subscribe<K extends keyof WebSocketEventMap>(
        event: K,
        callback: Listener<K>
    ): () => void {
        const eventListeners = this.getListenersForEvent(event);
        eventListeners.add(callback);

        return () => {
            eventListeners.delete(callback);
        };
    }

    private emit<K extends keyof WebSocketEventMap>(
        event: K,
        data: WebSocketEventMap[K]
    ): void {
        this.listeners[event]?.forEach((cb) => cb(data));
    }
}

export const wsManager = new WebSocketManager();